import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Check, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import Layout from '../components/layout/Layout';
import SEO from '../components/shared/SEO';
import ContactForm from '../components/shared/ContactForm';
import NewsletterSignup from '../components/shared/NewsletterSignup';
import { heroContent, brandApproach, tools, siteConfig } from '../data/mock';

const HomePage = () => {
  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: 'https://summarygpt.com',
      },
    ],
  };

  return (
    <Layout>
      <SEO
        title="AI That Delivers"
        description={siteConfig.description}
        path="/"
        schema={breadcrumbSchema}
      />

      {/* ============================================
          HERO - Brand Focus, ONE Message, ONE CTA
          ============================================ */}
      <section className="relative min-h-[88vh] flex items-center pt-20 overflow-hidden">
        {/* Additional glow for hero */}
        <div className="absolute inset-0 sgpt-glow opacity-80 pointer-events-none" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="max-w-3xl">
            {/* Brand Mark */}
            <div className="flex items-center gap-3 mb-10 animate-fade-in">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shadow-lg shadow-amber-500/25">
                <Sparkles className="w-6 h-6 text-white" />
              </div>
              <span className="text-xl font-semibold text-white">SummaryGPT</span>
            </div>

            {/* Headline */}
            <h1 className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-bold text-white leading-[1.05] mb-8 animate-fade-in-up">
              {heroContent.headline}
            </h1>

            {/* Subheadline */}
            <p className="text-xl md:text-2xl text-slate-400 max-w-2xl mb-12 leading-relaxed animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
              {heroContent.subheadline}
            </p>

            {/* ONE Clear CTA */}
            <div className="animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
              <Link to={heroContent.cta.url}>
                <Button className="h-14 px-10 text-lg bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 text-slate-900 font-semibold shadow-xl shadow-amber-500/20 hover:shadow-amber-500/35 hover:scale-[1.02] transition-all duration-300 border-0 gap-3 rounded-xl">
                  {heroContent.cta.text}
                  <ArrowRight className="w-5 h-5" />
                </Button>
              </Link>
            </div>
          </div>
        </div>

      </section>

      {/* ============================================
          OUR APPROACH - Option A (Tool Agnostic)
          ============================================ */}
      <section className="relative py-28 md:py-36 overflow-hidden">
        <div className="absolute inset-0 sgpt-glow-subtle opacity-50 pointer-events-none" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Section header */}
          <div className="max-w-2xl mb-20">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              {brandApproach.headline}
            </h2>
          </div>

          {/* Principles - Clean cards */}
          <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
            {brandApproach.principles.map((principle, index) => (
              <div
                key={principle.id}
                className="group relative p-8 lg:p-10 rounded-2xl glass-card glass-interactive"
              >
                {/* Number */}
                <div className="text-6xl font-bold text-white/10 mb-6 group-hover:text-amber-500/20 transition-colors duration-300">
                  {principle.id}
                </div>
                <h3 className="text-xl lg:text-2xl font-bold text-white mb-4">
                  {principle.title}
                </h3>
                <p className="text-slate-400 leading-relaxed">
                  {principle.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Section Divider */}
      <div className="section-divider mx-auto max-w-5xl" />

      {/* ============================================
          TOOLS - Each with dedicated space
          ============================================ */}
      <section className="relative py-28 md:py-36 overflow-hidden">
        <div className="absolute inset-0 sgpt-glow-subtle opacity-30 pointer-events-none" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Section header */}
          <div className="text-center mb-20">
            <p className="text-amber-400 font-semibold mb-4 tracking-wide uppercase text-sm">What We Build</p>
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-6">
              One problem. Fully solved. Every time.
            </h2>
            <p className="text-xl text-slate-400 max-w-2xl mx-auto">
              Each tool is purpose-built for a specific job.
            </p>
          </div>

          {/* Tools */}
          <div className="space-y-8">
            {tools.map((tool) => {
              const isLive = tool.status === 'live';
              
              return (
                <div
                  key={tool.id}
                  className="group relative rounded-2xl glass-card glass-interactive overflow-hidden"
                >
                  <div className="relative p-8 md:p-12">
                    <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
                      {/* Content */}
                      <div>
                        <div className="flex items-center gap-4 mb-6">
                          <h3 className="text-3xl md:text-4xl font-bold text-white group-hover:text-amber-400 transition-colors duration-300">
                            {tool.name}
                          </h3>
                          {isLive ? (
                            <span className="px-3 py-1 text-xs font-semibold bg-emerald-500/15 text-emerald-400 rounded-full border border-emerald-500/25">
                              Live
                            </span>
                          ) : (
                            <span className="px-3 py-1 text-xs font-semibold bg-amber-500/15 text-amber-400 rounded-full border border-amber-500/25">
                              Coming Soon
                            </span>
                          )}
                        </div>
                        
                        <p className="text-2xl text-slate-300 font-medium mb-4">
                          {tool.tagline}
                        </p>
                        
                        <p className="text-slate-400 mb-6 leading-relaxed">
                          {tool.description}
                        </p>

                        <p className="text-sm text-slate-500 italic mb-8">
                          {tool.highlight}
                        </p>

                        {/* CTAs */}
                        <div className="flex flex-wrap gap-4">
                          {isLive && tool.appUrl ? (
                            <a href={tool.appUrl} target="_blank" rel="noopener noreferrer">
                              <Button className="h-12 px-6 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 text-slate-900 font-semibold shadow-lg shadow-amber-500/15 hover:shadow-amber-500/25 transition-all duration-300 border-0 gap-2 rounded-xl">
                                Try {tool.name}
                                <ArrowRight className="w-4 h-4" />
                              </Button>
                            </a>
                          ) : (
                            <a href={`mailto:hello@summarygpt.com?subject=${tool.name}%20Early%20Access`}>
                              <Button className="h-12 px-6 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 text-slate-900 font-semibold shadow-lg shadow-amber-500/15 hover:shadow-amber-500/25 transition-all duration-300 border-0 gap-2 rounded-xl">
                                Get Early Access
                                <ArrowRight className="w-4 h-4" />
                              </Button>
                            </a>
                          )}
                          <Link to={tool.internalUrl}>
                            <Button variant="outline" className="h-12 px-6 bg-white/5 border-white/10 text-slate-300 hover:bg-white/10 hover:text-white hover:border-white/20 transition-all duration-300 gap-2 rounded-xl">
                              Learn more
                            </Button>
                          </Link>
                        </div>
                      </div>

                      {/* Benefits sidebar */}
                      <div className="hidden lg:block">
                        <div className="rounded-xl bg-black/20 border border-white/5 p-6">
                          <p className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-4">What you get</p>
                          <ul className="space-y-3">
                            {tool.whatYouGet.slice(0, 4).map((item, i) => (
                              <li key={i} className="flex items-start gap-3 text-slate-300">
                                <Check className="w-5 h-5 text-amber-400 mt-0.5 flex-shrink-0" />
                                <span>{item}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Section Divider */}
      <div className="section-divider mx-auto max-w-5xl" />

      {/* ============================================
          NEWSLETTER SIGNUP
          ============================================ */}
      <section className="relative py-28 md:py-36 overflow-hidden">
        <div className="absolute inset-0 sgpt-glow opacity-40 pointer-events-none" />
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <NewsletterSignup />
        </div>
      </section>

      {/* Section Divider */}
      <div className="section-divider mx-auto max-w-5xl" />

      {/* ============================================
          CONTACT US
          ============================================ */}
      <section id="contact" className="relative py-28 md:py-36 overflow-hidden">
        <div className="absolute inset-0 sgpt-glow-subtle opacity-30 pointer-events-none" />
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <ContactForm />
        </div>
      </section>
    </Layout>
  );
};

export default HomePage;
